// Service Templates for Quick Business Setup
// Pre-filled service catalogs for different business types

// Service Option - customizable option with name and value (e.g., "Size", "Color")
export interface ServiceOption {
  name: string; // e.g., "Size", "Style", "Add-on"
  value: string; // e.g., "Large", "Premium", "Yes"
}

export interface Service {
  id?: string;
  name: string;
  description: string;
  /** null = price on request */
  price: number | null;
  duration: number; // in minutes
  category: string;
  subcategory?: string; // e.g., 'Men', 'Women', 'Basic', 'Premium'
  isActive: boolean;
  requirements: string[];
  icon: string;
  options?: ServiceOption[]; // Up to 3 customizable options
}

export interface ServiceTemplate {
  businessType: string;
  categoryId: string;
  categoryName: string;
  icon: string;
  services: Omit<Service, 'id'>[];
}

export const SERVICE_TEMPLATES: ServiceTemplate[] = [
  // Hair Salon & Beauty Template
  {
    businessType: 'hair_salon',
    categoryId: 'hair_beauty',
    categoryName: 'Hair Salon & Beauty',
    icon: '💇‍♀️',
    services: [
      {
        name: 'Haircut & Styling',
        description: 'Professional haircut and styling for men and women',
        price: 45.00,
        duration: 60,
        category: 'Hair & Beauty',
        isActive: true,
        requirements: ['Clean hair', 'No styling products'],
        icon: '💇‍♀️',
      },
      {
        name: 'Hair Color',
        description: 'Full hair coloring service with consultation',
        price: 85.00,
        duration: 120,
        category: 'Hair & Beauty',
        subcategory: 'Color',
        isActive: true,
        requirements: ['Clean hair', 'No previous color', 'Patch test required'],
        icon: '🎨',
        options: [
          { name: 'Color Type', value: 'Full Color' },
          { name: 'Shade', value: 'Natural' },
          { name: 'Treatment', value: 'Standard' }
        ],
      },
      {
        name: 'Highlights',
        description: 'Professional hair highlighting service',
        price: 95.00,
        duration: 150,
        category: 'Hair & Beauty',
        isActive: true,
        requirements: ['Clean hair', 'Consultation required'],
        icon: '✨',
      },
      {
        name: 'Hair Treatment',
        description: 'Deep conditioning and repair treatment',
        price: 55.00,
        duration: 45,
        category: 'Hair & Beauty',
        isActive: true,
        requirements: ['Clean hair'],
        icon: '💆‍♀️',
      },
      {
        name: 'Manicure',
        description: 'Professional nail care and polish',
        price: 35.00,
        duration: 45,
        category: 'Hair & Beauty',
        isActive: true,
        requirements: ['Clean hands', 'No nail polish'],
        icon: '💅',
      },
      {
        name: 'Pedicure',
        description: 'Professional foot care and polish',
        price: 45.00,
        duration: 60,
        category: 'Hair & Beauty',
        isActive: true,
        requirements: ['Clean feet'],
        icon: '🦶',
      },
      {
        name: 'Facial',
        description: 'Deep cleansing facial treatment',
        price: 75.00,
        duration: 60,
        category: 'Hair & Beauty',
        isActive: true,
        requirements: ['No makeup', 'Clean face'],
        icon: '✨',
      },
      {
        name: 'Eyebrow Shaping',
        description: 'Professional eyebrow threading or waxing',
        price: 25.00,
        duration: 30,
        category: 'Hair & Beauty',
        isActive: true,
        requirements: ['No makeup on brows'],
        icon: '👁️',
      },
    ],
  },

  // Spa & Wellness Template
  {
    businessType: 'spa_wellness',
    categoryId: 'health_wellness',
    categoryName: 'Spa & Wellness',
    icon: '🧘‍♀️',
    services: [
      {
        name: 'Swedish Massage',
        description: 'Relaxing full-body Swedish massage',
        price: 80.00,
        duration: 60,
        category: 'Health & Wellness',
        isActive: true,
        requirements: ['Arrive 10 minutes early', 'Wear comfortable clothing'],
        icon: '💆‍♀️',
      },
      {
        name: 'Deep Tissue Massage',
        description: 'Therapeutic deep tissue massage',
        price: 95.00,
        duration: 60,
        category: 'Health & Wellness',
        isActive: true,
        requirements: ['Arrive 10 minutes early', 'Inform of any injuries'],
        icon: '💪',
      },
      {
        name: 'Hot Stone Massage',
        description: 'Relaxing hot stone therapy massage',
        price: 110.00,
        duration: 75,
        category: 'Health & Wellness',
        isActive: true,
        requirements: ['Arrive 10 minutes early'],
        icon: '🔥',
      },
      {
        name: 'Aromatherapy Session',
        description: 'Essential oil therapy session',
        price: 70.00,
        duration: 50,
        category: 'Health & Wellness',
        isActive: true,
        requirements: ['Inform of any allergies'],
        icon: '🌸',
      },
      {
        name: 'Body Wrap',
        description: 'Detoxifying body wrap treatment',
        price: 85.00,
        duration: 60,
        category: 'Health & Wellness',
        isActive: true,
        requirements: ['Shower before appointment'],
        icon: '🧖‍♀️',
      },
      {
        name: 'Facial Treatment',
        description: 'Deep cleansing and hydrating facial',
        price: 75.00,
        duration: 60,
        category: 'Health & Wellness',
        isActive: true,
        requirements: ['No makeup', 'Clean face'],
        icon: '✨',
      },
    ],
  },

  // Automotive Services Template
  {
    businessType: 'automotive',
    categoryId: 'automotive',
    categoryName: 'Automotive Services',
    icon: '🚗',
    services: [
      {
        name: 'Oil Change',
        description: 'Standard oil change service',
        price: 35.00,
        duration: 30,
        category: 'Automotive',
        subcategory: 'Maintenance',
        isActive: true,
        requirements: ['Bring vehicle', 'Appointment confirmation'],
        icon: '🛢️',
        options: [
          { name: 'Oil Type', value: 'Conventional' },
          { name: 'Filter', value: 'Standard' },
          { name: 'Service Level', value: 'Basic' }
        ],
      },
      {
        name: 'Tire Rotation',
        description: 'Professional tire rotation service',
        price: 25.00,
        duration: 30,
        category: 'Automotive',
        isActive: true,
        requirements: ['Bring vehicle'],
        icon: '🛞',
      },
      {
        name: 'Brake Inspection',
        description: 'Comprehensive brake system inspection',
        price: 50.00,
        duration: 45,
        category: 'Automotive',
        isActive: true,
        requirements: ['Bring vehicle'],
        icon: '🛑',
      },
      {
        name: 'Battery Replacement',
        description: 'Car battery replacement service',
        price: 120.00,
        duration: 30,
        category: 'Automotive',
        isActive: true,
        requirements: ['Bring vehicle', 'Old battery'],
        icon: '🔋',
      },
      {
        name: 'AC Service',
        description: 'Air conditioning system service',
        price: 80.00,
        duration: 60,
        category: 'Automotive',
        isActive: true,
        requirements: ['Bring vehicle'],
        icon: '❄️',
      },
      {
        name: 'Engine Diagnostic',
        description: 'Complete engine diagnostic check',
        price: 75.00,
        duration: 45,
        category: 'Automotive',
        isActive: true,
        requirements: ['Bring vehicle'],
        icon: '🔧',
      },
    ],
  },

  // Home Services Template
  {
    businessType: 'home_services',
    categoryId: 'home_services',
    categoryName: 'Home Services',
    icon: '🏠',
    services: [
      {
        name: 'House Cleaning',
        description: 'Complete house cleaning service',
        price: 120.00,
        duration: 180,
        category: 'Home Services',
        isActive: true,
        requirements: ['Provide access', 'Clear work areas'],
        icon: '🧹',
      },
      {
        name: 'Deep Cleaning',
        description: 'Thorough deep cleaning service',
        price: 200.00,
        duration: 240,
        category: 'Home Services',
        isActive: true,
        requirements: ['Provide access', 'Clear work areas'],
        icon: '✨',
      },
      {
        name: 'Window Cleaning',
        description: 'Interior and exterior window cleaning',
        price: 80.00,
        duration: 90,
        category: 'Home Services',
        isActive: true,
        requirements: ['Provide access'],
        icon: '🪟',
      },
      {
        name: 'Carpet Cleaning',
        description: 'Professional carpet cleaning service',
        price: 150.00,
        duration: 120,
        category: 'Home Services',
        isActive: true,
        requirements: ['Clear furniture', 'Provide access'],
        icon: '🧼',
      },
      {
        name: 'Plumbing Service',
        description: 'General plumbing repair and maintenance',
        price: 100.00,
        duration: 60,
        category: 'Home Services',
        isActive: true,
        requirements: ['Describe issue', 'Provide access'],
        icon: '🔧',
      },
      {
        name: 'Electrical Service',
        description: 'Electrical repair and installation',
        price: 120.00,
        duration: 90,
        category: 'Home Services',
        isActive: true,
        requirements: ['Describe issue', 'Provide access'],
        icon: '⚡',
      },
    ],
  },

  // Professional Services Template
  {
    businessType: 'professional_services',
    categoryId: 'professional_services',
    categoryName: 'Professional Services',
    icon: '💼',
    services: [
      {
        name: 'Consultation',
        description: 'Initial consultation session',
        price: 50.00,
        duration: 60,
        category: 'Professional Services',
        isActive: true,
        requirements: ['Bring relevant documents'],
        icon: '💬',
      },
      {
        name: 'Legal Consultation',
        description: 'Legal advice and consultation',
        price: 150.00,
        duration: 60,
        category: 'Professional Services',
        isActive: true,
        requirements: ['Bring relevant documents', 'Prepare questions'],
        icon: '⚖️',
      },
      {
        name: 'Tax Preparation',
        description: 'Tax return preparation service',
        price: 200.00,
        duration: 90,
        category: 'Professional Services',
        isActive: true,
        requirements: ['Bring tax documents', 'W-2 forms'],
        icon: '📊',
      },
      {
        name: 'Financial Planning',
        description: 'Personal financial planning session',
        price: 150.00,
        duration: 90,
        category: 'Professional Services',
        isActive: true,
        requirements: ['Bring financial documents'],
        icon: '💰',
      },
      {
        name: 'Business Consulting',
        description: 'Business strategy and consulting',
        price: 200.00,
        duration: 120,
        category: 'Professional Services',
        isActive: true,
        requirements: ['Prepare business questions'],
        icon: '📈',
      },
    ],
  },

  // Halal Services Template
  {
    businessType: 'halal_services',
    categoryId: 'halal_services',
    categoryName: 'Halal Services',
    icon: '🥩',
    services: [
      {
        name: 'Halal Meat Consultation',
        description: 'Consultation on halal meat products and sourcing',
        price: 50.00,
        duration: 60,
        category: 'Halal Services',
        isActive: true,
        requirements: ['Bring questions', 'Appointment confirmation'],
        icon: '🥩',
      },
      {
        name: 'Halal Certification Review',
        description: 'Review and guidance on halal certification',
        price: 100.00,
        duration: 90,
        category: 'Halal Services',
        isActive: true,
        requirements: ['Bring documents'],
        icon: '✅',
      },
      {
        name: 'Halal Food Preparation',
        description: 'Halal food preparation and cooking service',
        price: 150.00,
        duration: 180,
        category: 'Halal Services',
        isActive: true,
        requirements: ['Provide ingredients', 'Kitchen access'],
        icon: '🍽️',
      },
    ],
  },

  // General Services Template
  {
    businessType: 'general',
    categoryId: 'other',
    categoryName: 'General Services',
    icon: '⭐',
    services: [
      {
        name: 'Basic Consultation',
        description: 'Initial consultation and assessment',
        price: 25.00,
        duration: 30,
        category: 'Other',
        isActive: true,
        requirements: ['Arrive 10 minutes early'],
        icon: '💬',
      },
      {
        name: 'Custom Service',
        description: 'Customized service based on your needs',
        price: 50.00,
        duration: 60,
        category: 'Other',
        isActive: true,
        requirements: ['Contact for details'],
        icon: '🎯',
      },
    ],
  },
];

// Helper function to get template by business type
export const getServiceTemplateByBusinessType = (businessType: string): ServiceTemplate | undefined => {
  return SERVICE_TEMPLATES.find(template => template.businessType === businessType);
};

// Helper function to map industry/category to business type
export const mapIndustryToBusinessType = (industry: string): string => {
  const industryLower = industry.toLowerCase().trim();
  
  // Map common industry names to template business types
  if (industryLower.includes('hair') || industryLower.includes('salon') || industryLower.includes('beauty') || industryLower.includes('barber')) {
    return 'hair_salon';
  }
  if (industryLower.includes('spa') || industryLower.includes('wellness') || industryLower.includes('massage') || industryLower.includes('therapy')) {
    return 'spa_wellness';
  }
  if (industryLower.includes('automotive') || industryLower.includes('car') || industryLower.includes('auto') || industryLower.includes('vehicle')) {
    return 'automotive';
  }
  if (industryLower.includes('home') || industryLower.includes('cleaning') || industryLower.includes('plumbing') || industryLower.includes('electrical')) {
    return 'home_services';
  }
  if (industryLower.includes('legal') || industryLower.includes('consulting') || industryLower.includes('professional') || industryLower.includes('accounting') || industryLower.includes('tax')) {
    return 'professional_services';
  }
  if (industryLower.includes('halal') || industryLower.includes('islamic')) {
    return 'halal_services';
  }
  
  // Default to general if no match
  return 'general';
};

// Helper function to get template by industry
export const getServiceTemplateByIndustry = (industry: string): ServiceTemplate | undefined => {
  const businessType = mapIndustryToBusinessType(industry);
  return getServiceTemplateByBusinessType(businessType);
};

// Helper function to get all available templates
export const getAllServiceTemplates = (): ServiceTemplate[] => {
  return SERVICE_TEMPLATES;
};

// Helper function to create services from template
export const createServicesFromTemplate = (
  template: ServiceTemplate,
  businessId: string
): Service[] => {
  return template.services.map((service, index) => ({
    ...service,
    id: `service_${businessId}_${Date.now()}_${index}`,
  }));
};

export default SERVICE_TEMPLATES;

